import { useEffect, useMemo, useState } from "react";

import { NotificationType, UserPreferences } from "@asd/customer-portal-api";
import {
  fromNotificationType,
  getPreferenceRows,
  phoneMaskOptions,
  phoneValidatorOptional,
  toDisplayPhone,
  toNotificationType,
  toStoredPhone,
  useOrganization,
} from "@asd/customer-portal-ui";
import LaptopIcon from "@mui/icons-material/Laptop";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMask } from "@react-input/mask";
import { Controller, useForm, useWatch } from "react-hook-form";

import { UserService } from "~/services";
import { env } from "~/utils/env";
import { useKeycloak } from "../providers/KeycloakProvider";

interface PreferencesFormValues extends UserPreferences {
  phoneNumber: string;
}

export const useUserPreferences = () => {
  const keycloak = useKeycloak();
  const {
    configuration: {
      branding: { name, withTM },
    },
  } = useOrganization();

  const [isUserPreferencesSet, setIsUserPreferencesSet] = useState<null | boolean>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  const preferenceRows = useMemo(() => getPreferenceRows({ roles: keycloak.realmAccess?.roles ?? [] }), [keycloak]);

  const phoneInputRef = useMask(phoneMaskOptions);

  const {
    control,
    reset,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<PreferencesFormValues>({
    defaultValues: {
      ...Object.fromEntries(preferenceRows.map(({ key }) => [key, NotificationType.ALL])),
      phoneNumber: "",
    } as PreferencesFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const phoneHasError = !!errors.phoneNumber;
  const watchedValues = useWatch({ control });

  const anySmsEnabled = useMemo(
    () =>
      preferenceRows.some(({ key }) => {
        const val = watchedValues[key] as NotificationType | undefined;
        return val === NotificationType.SMS || val === NotificationType.ALL;
      }),
    [watchedValues, preferenceRows],
  );

  const onSubmit = async (data: PreferencesFormValues) => {
    setHasError(false);
    try {
      setIsSaving(true);

      const visiblePrefs = Object.fromEntries(preferenceRows.map(({ key }) => [key, data[key]])) as unknown as UserPreferences;

      await UserService.updateUserPreferences(visiblePrefs);

      if (data.phoneNumber) {
        const token = keycloak.token;
        const phoneResponse = await fetch(`${env.REACT_APP_KEYCLOAK_URL}/realms/customer-portal-realm/account`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: keycloak.userInfo.given_name,
            lastName: keycloak.userInfo.family_name,
            email: keycloak.userInfo.email,
            attributes: {
              phoneNumber: [toStoredPhone(data.phoneNumber)],
            },
          }),
        });
        if (!phoneResponse.ok) throw new Error("Failed to update phone number");

        await UserService.getUserState();
        await keycloak.loadUserInfo();
      }

      setIsUserPreferencesSet(true);
    } catch {
      setHasError(true);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!keycloak.authenticated) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    (async () => {
      setHasError(false);
      try {
        const response = await UserService.getUserPreferences({ signal: abortController.signal });

        if (abortController.signal.aborted) return;

        if (response?.status === 200 && response?.data) {
          const normalized = Object.fromEntries(
            Object.entries(response.data).map(([key, value]) => [
              key,
              preferenceRows.some((row) => row.key === key) && value === NotificationType.UNSET ? NotificationType.ALL : value,
            ]),
          ) as unknown as UserPreferences;

          reset({
            ...normalized,
            phoneNumber: toDisplayPhone(keycloak.userInfo?.phone_number),
          });

          setIsUserPreferencesSet(!preferenceRows.some(({ key }) => response.data[key] === NotificationType.UNSET));
          setTimeout(() => {
            trigger("phoneNumber");
          }, 0);
        } else {
          throw new Error("Failed to retrieve user preferences");
        }
      } catch (err: any) {
        if (err.name === "CanceledError" || err.name === "AbortError" || abortController.signal.aborted) return;
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [keycloak, reset, preferenceRows, trigger]);

  useEffect(() => {
    if (watchedValues) {
      trigger("phoneNumber");
    }
  }, [anySmsEnabled, trigger, watchedValues]);

  if (!keycloak.authenticated || isLoading) {
    return undefined;
  }

  if (isUserPreferencesSet !== false) {
    return false;
  }

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <Dialog
        slotProps={{
          root: {
            sx: { position: "absolute" },
          },
          paper: {
            sx: { outline: "none", m: 0 },
          },
        }}
        disablePortal
        disableRestoreFocus
        hideBackdrop
        open
      >
        {!!isSaving && (
          <Box
            sx={(theme) => ({
              width: "100%",
              height: "100%",
              position: "absolute",
              alignContent: "center",
              textAlign: "center",
              background: alpha(theme.palette.background.paper, 0.6),
              zIndex: theme.zIndex.drawer - 1,
            })}
          >
            <CircularProgress size={150} />
            <Typography sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>Saving...</Typography>
          </Box>
        )}
        <DialogTitle align="center" color="custom.main" fontWeight="bold" variant="h4" gutterBottom>
          Communication Preferences
        </DialogTitle>
        <DialogContent>
          <Typography>Please set your communication preferences</Typography>
          <Stack
            component="form"
            direction="column"
            flexGrow={1}
            height="100%"
            id="user-preferences-form"
            onSubmit={handleSubmit(onSubmit)}
            pt={1}
            noValidate
          >
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Preference</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      <Stack alignItems="center" direction="row" justifyContent="center" spacing={0.5}>
                        <PhoneIphoneIcon fontSize="small" />
                        <Typography variant="inherit">SMS</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      <Stack alignItems="center" direction="row" justifyContent="center" spacing={0.5}>
                        <LaptopIcon fontSize="small" />
                        <Typography variant="inherit">Email</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preferenceRows.map(({ key, label }) => (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <Controller
                        control={control}
                        name={key}
                        render={({ field }) => {
                          const { sms, email } = fromNotificationType(field.value);
                          return (
                            <>
                              <TableCell align="center">
                                <Checkbox
                                  checked={sms}
                                  onChange={(e) => field.onChange(toNotificationType(e.target.checked, email))}
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={email}
                                  onChange={(e) => field.onChange(toNotificationType(sms, e.target.checked))}
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                            </>
                          );
                        }}
                      />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  helperText={
                    error
                      ? error.message
                      : anySmsEnabled
                        ? "Required for SMS notifications"
                        : "Optional — only required if SMS notifications are enabled"
                  }
                  inputRef={phoneInputRef}
                  label="Phone Number"
                  required={anySmsEnabled}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalPhoneIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ mt: 2 }}
                  type="text"
                />
              )}
              rules={{
                required: anySmsEnabled ? "Required for SMS notifications" : false,
                validate: phoneValidatorOptional.validate,
              }}
            />
            <Typography color="text.secondary" sx={{ px: 5, mt: 1, textAlign: "center" }} variant="body2">
              By checking the SMS boxes above, you agree to receive automated SMS messages from {name}
              {withTM && (
                <Typography component="sup" fontSize="xx-small" variant="caption">
                  TM
                </Typography>
              )}
              . Message frequency varies. Message and data rates may apply. You may reply STOP to opt out of SMS at any time. For more
              information, view our Privacy Policy and Terms of Service.
            </Typography>
            {!!hasError && (
              <Typography color="error" sx={{ mt: 2 }}>
                Error saving preferences, please try again or contact customer service
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={phoneHasError || (anySmsEnabled && !watchedValues.phoneNumber)} form="user-preferences-form" type="submit">
            {hasError ? "Try Again" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
